import { Injectable, Injector } from '@angular/core';
import { Storage } from '@ionic/storage';
import { MyService } from '../providers/myService';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { UserSignup, UserLogin } from '../interfaces/user-options';

declare const grecaptcha: any;

@Injectable({
  providedIn: 'root'
})
export class UserData {
  favorites: string[] = [];
  HAS_LOGGED_IN = 'hasLoggedIn';
  HAS_SEEN_TUTORIAL = 'hasSeenTutorial';
  private readyPromise: Promise<void>;
  private routerInstance: Router | null = null;

  constructor(
    public storage: Storage,
    public MyService: MyService,
    private injector: Injector,
    public alertCtrl: AlertController,
  ) {
    // Al iniciar el servicio, verificamos si existe una sesión externa en cookies
    this.readyPromise = this.checkExternalSession();
  }

  private getRouter(): Router {
    if (!this.routerInstance) {
      this.routerInstance = this.injector.get(Router);
    }
    return this.routerInstance;
  }

  onReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Sincroniza la sesión si el usuario viene logueado desde el puerto principal
   */
  async checkExternalSession() {
    await this.storage.ready();

    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const lastPart = parts.pop();
        return lastPart ? decodeURIComponent(lastPart.split(';')[0]) : null;
      }
      return null;
    };

    const userId = getCookie('Id');
    const localUser = await this.getUserData();
    const localUserId = localUser ? (localUser.Id || localUser.user_Id) : null;
    const loggedIn = await this.isLoggedIn();

    if (userId) {
      // Si hay un ID en la cookie y no coincide con la sesión local (o no está logueado)
      if (!loggedIn || String(localUserId) !== String(userId)) {
        const userDataObj = {
          user_Id: userId,
          Id: userId,
          Username: getCookie('Username'),
          Name: getCookie('Profile'),
          First_Name: getCookie('First_Name'),
          Last_Name: getCookie('Last_Name'),
          Image: getCookie('Image'),
          Hour_Zone: getCookie('Hour_Zone'),
        };

        console.log("Found external cookie session, setting up local storage...", userDataObj);
        await this.storage.set(this.HAS_LOGGED_IN, true);
        await Promise.all([
          this.setUsername(userDataObj.First_Name + ' ' + userDataObj.Last_Name),
          this.setUserData(userDataObj)
        ]);

        window.dispatchEvent(new CustomEvent('user:login'));
      }
    } else {
      // Si no hay cookie pero localmente está como logueado, limpiamos la sesión
      if (loggedIn) {
        console.log("No external cookie found but local session is active, logging out...");
        await this.storage.remove(this.HAS_LOGGED_IN);
        await Promise.all([
          this.storage.remove("userdata"),
          this.storage.remove("username")
        ]);
        window.dispatchEvent(new CustomEvent('user:logout'));
      }
    }
  }

  hasFavorite(sessionName: string): boolean {
    return (this.favorites.indexOf(sessionName) > -1);
  }

  addFavorite(sessionName: string): void {
    this.favorites.push(sessionName);
  }

  removeFavorite(sessionName: string): void {
    const index = this.favorites.indexOf(sessionName);
    if (index > -1) {
      this.favorites.splice(index, 1);
    }
  }

  login(userdata: UserLogin) {
    grecaptcha.ready(() => {
      grecaptcha.execute('6LezkC0sAAAAAMd3lDexK9HjTtK3m84xJg4BlSB4', { action: 'loginClient' })
        .then((token: string) => {
          userdata.recaptcha_token = token;
          this.MyService.apiData('POST', '/api/loginClient', null, userdata).subscribe(
            async (result) => {
              if (typeof result[0] === 'object' && result[0] !== null) {
                console.log(result[0]);
                await this.storage.set(this.HAS_LOGGED_IN, true);
                const name = result[0]['First_Name'] + ' ' + result[0]['Last_Name'];
                await Promise.all([
                  this.setUsername(name),
                  this.setUserData(result[0])
                ]);
                window.dispatchEvent(new CustomEvent('user:login'));
                window.location.href = "/dashboard";
              } else {
                this.presentErrorAlert(result);
              }
            },
            (error) => { console.log(error); }
          );
        });
    });
  }

  signup(userdata: UserSignup) {
    grecaptcha.ready(() => {
      grecaptcha.execute('6LezkC0sAAAAAMd3lDexK9HjTtK3m84xJg4BlSB4', { action: 'clientRegister' })
        .then((token: string) => {
          userdata.recaptcha_token = token;
          this.MyService.apiData('POST', '/api/clientRegister', null, userdata)
            .subscribe(
              (result) => {
                if (result === 'Client Registration is successfully added, now you have to check the email to confirm your account.') {
                  this.presentSuccessAlert(result, userdata);
                  window.dispatchEvent(new CustomEvent('user:signup'));
                } else {
                  this.presentErrorAlert(result);
                }
              },
              (error) => {
                console.log(error.error.errors);
                let obj = JSON.stringify(error.error.errors);
                let obj2 = obj.split(/[\{,\},\:,\",\[,\]]+/);
                let obj3 = [];
                for (var i = 2; i < obj2.length; i += 2) {
                  obj3.push(obj2[i]);
                }
                this.presentErrorAlert(
                  `<ul style="text-align: left; padding-left: 20px;">
                  ${obj3.map(err => `<li>${err}</li>`).join('')}
                  </ul>`);
              }
            );
        });
    });
  }

  logout(): Promise<any> {
    const getCookieDomain = () => {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('192.168.') || !hostname.includes('.')) {
        return '';
      }
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        return `domain=.${parts.slice(-2).join('.')};`;
      }
      return '';
    };

    return this.storage.remove(this.HAS_LOGGED_IN).then(async () => {
      await Promise.all([
        this.storage.remove("userdata"),
        this.storage.remove("username")
      ]);

      const cookieDomain = getCookieDomain();
      const cookieBase = `path=/; ${cookieDomain} expires=Thu, 01 Jan 1970 00:00:00 GMT;`;

      document.cookie = `Id=; ${cookieBase}`;
      document.cookie = `Username=; ${cookieBase}`;
      document.cookie = `Profile=; ${cookieBase}`;
      document.cookie = `First_Name=; ${cookieBase}`;
      document.cookie = `FirstName=; ${cookieBase}`;
      document.cookie = `Last_Name=; ${cookieBase}`;
      document.cookie = `LastName=; ${cookieBase}`;
      document.cookie = `Image=; ${cookieBase}`;
      document.cookie = `UserImage=; ${cookieBase}`;
      document.cookie = `User_Image=; ${cookieBase}`;
      document.cookie = `Hour_Zone=; ${cookieBase}`;
      document.cookie = `HourZone=; ${cookieBase}`;
    }).then(() => {
      window.dispatchEvent(new CustomEvent('user:logout'));
      window.location.href = "https://walba.app";
    });
  }

  setUsername(username: string): Promise<any> {
    return this.storage.set('username', username);
  }

  getUsername(): Promise<string> {
    return this.storage.get('username').then((value) => {
      return value;
    });
  }

  setUserData(userdata: any): Promise<any> {
    return this.storage.set('userdata', userdata);
  }

  getUserData(): Promise<any> {
    return this.storage.get('userdata').then((value) => {
      return value;
    });
  }

  isLoggedIn(): Promise<boolean> {
    return this.storage.get(this.HAS_LOGGED_IN).then((value) => {
      return value === true;
    });
  }

  checkHasSeenTutorial(): Promise<string> {
    return this.storage.get(this.HAS_SEEN_TUTORIAL).then((value) => {
      return value;
    });
  }

  async presentErrorAlert(message: any) {
    const errorAlert = await this.alertCtrl.create({
      header: 'Validation Error',
      message: message,
      buttons: ['OK']
    });
    await errorAlert.present();
  };

  async presentSuccessAlert(message: any, userdata?: UserSignup) {
    if (userdata) {
      userdata.First_Name = '';
      userdata.Last_Name = '';
      userdata.Username = '';
      userdata.Email = '';
      userdata.Phone = null;
      userdata.Password = '';
      userdata.Password2 = '';
      userdata.agreementStatus = false;
      userdata.Date_Birth = '';
    }
    const successAlert = await this.alertCtrl.create({
      header: '',
      message: '✅ ' + message,
      buttons: [
        {
          text: 'OK',
          handler: () => {
            this.getRouter().navigate(['/login']);
          }
        }
      ]
    });
    await successAlert.present();
  }
}
